import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import HotelMapUrlField from '@/components/HotelMapUrlField';
import { Compass, Plus, Trash2, Sparkles } from 'lucide-react';

/**
 * Per-day activity selector for the tour itinerary card.
 * View mode: compact pill list of activity names scheduled on this day.
 * Edit mode: row-by-row editor that lets the operator pick from existing
 *            Activity vendors (autocomplete) OR type a brand-new activity
 *            name to create one inline. Selecting an existing vendor
 *            attaches its initiatives so the share map can render the
 *            "✨ A Pollen Dot of Care" halo.
 *
 * Storage shape on the tour: `activities[]` (top-level on the tour) — each
 * activity has `{ activity_name, date, time_slot, contact_details, status,
 * total_amount, advance, balance, initiatives }`. We append/remove by
 * `date` to keep the editor scoped to a single day.
 */

const COL = { 'smiles': '#f59e0b', 'cultural-exchange': '#c2410c', 'sustainability': '#84cc16' };

const DayActivitiesEditor = ({ dayDate, editing, editData, updateEdit, vendors }) => {
  const allActivities = editData?.activities || [];
  const dayItems = useMemo(
    () => allActivities.map((a, idx) => ({ ...a, _idx: idx })).filter(a => a.date === dayDate),
    [allActivities, dayDate]
  );

  const setActivities = (next) => updateEdit('activities', next);

  const addActivity = (vendor) => {
    const next = [...allActivities, {
      activity_name: vendor?.name || '',
      date: dayDate,
      time_slot: '',
      contact_details: vendor?.phone || '',
      status: 'Pending',
      total_amount: 0,
      advance: 0,
      balance: 0,
      initiatives: vendor?.initiatives || [],
      vendor_id: vendor?.id || '',
    }];
    setActivities(next);
  };

  const removeActivity = (idx) => setActivities(allActivities.filter((_, i) => i !== idx));

  const updateActivity = (idx, patch) => setActivities(allActivities.map((a, i) => (i === idx ? { ...a, ...patch } : a)));

  if (!editing) {
    if (!dayItems.length) return null;
    return (
      <div className="flex items-start gap-2 flex-wrap pt-1">
        <span className="text-[11px] text-muted-foreground mt-0.5 shrink-0">Activities:</span>
        <div className="flex flex-wrap gap-1.5">
          {dayItems.map(a => (
            <span key={a._idx} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 border border-blue-200 text-blue-800 text-[11px]">
              <Compass className="w-3 h-3" />
              <span className="truncate max-w-[160px]">{a.activity_name || 'Activity'}</span>
              {a.time_slot && <span className="text-[10px] text-blue-600">· {a.time_slot}</span>}
              {(a.initiatives || []).slice(0, 3).map(k => (
                <span key={k} title={k} className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: COL[k] || '#94a3b8' }} />
              ))}
            </span>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2 border-t pt-2 mt-2" data-testid={`day-activities-editor-${dayDate}`}>
      <div className="flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">
          Activities {dayItems.length > 0 && `(${dayItems.length})`}
        </span>
      </div>
      {dayItems.length === 0 ? (
        <p className="text-[11px] text-muted-foreground italic">No activities yet. Search and add below.</p>
      ) : (
        <div className="space-y-1.5">
          {dayItems.map(a => (
            <ActivityRow
              key={a._idx}
              activity={a}
              vendors={vendors}
              onChange={(patch) => updateActivity(a._idx, patch)}
              onRemove={() => removeActivity(a._idx)}
            />
          ))}
        </div>
      )}
      <ActivityAdder vendors={vendors} onAdd={addActivity} dayDate={dayDate} />
    </div>
  );
};

const ActivityRow = ({ activity, vendors, onChange, onRemove }) => {
  const v = vendors.find(x => x.name && activity.vendor_id && x.id === activity.vendor_id);
  const inits = activity.initiatives || (v?.initiatives || []);
  return (
    <div className="p-2 rounded-md border bg-background space-y-1.5" data-testid={`day-activity-row`}>
      <div className="flex items-center gap-1.5">
        <Compass className="w-3.5 h-3.5 text-blue-600 shrink-0" />
        <Input
          value={activity.activity_name || ''}
          onChange={e => onChange({ activity_name: e.target.value })}
          className="h-7 text-xs flex-1"
          placeholder="Activity name"
          data-testid="day-activity-name"
        />
        <Input
          value={activity.time_slot || ''}
          onChange={e => onChange({ time_slot: e.target.value })}
          className="h-7 text-xs w-[90px]"
          placeholder="Time"
          data-testid="day-activity-time"
        />
        <Button type="button" size="sm" variant="ghost" className="h-7 w-7 p-0 text-destructive shrink-0" onClick={onRemove} data-testid="day-activity-remove">
          <Trash2 className="w-3 h-3" />
        </Button>
      </div>
      {/* Google Maps URL → resolves to lat/lng and pins the activity on the
          customer share map. Mirrors the activity form in Edit Tour. */}
      {(activity.activity_name || '').trim() && (
        <HotelMapUrlField
          entityName={activity.activity_name}
          entityType="activity"
          city={activity.location || ''}
          initialUrl={activity.maps_url || activity.map_url || activity.google_maps_url || ''}
          initialLat={activity.lat}
          initialLng={activity.lng}
          initialVerified={!!(activity.maps_url || activity.map_url || activity.google_maps_url)}
          onPinned={(p) => onChange({ maps_url: p.url, google_maps_url: p.url, lat: p.lat, lng: p.lng })}
          compact
          label=""
        />
      )}
      {/* Per-tour activity notes — surfaces inside the activity popup on the
          customer share map as the "✨ A Pollen Dot of Care" narrative. */}
      <Textarea
        rows={2}
        value={activity.notes || ''}
        onChange={e => onChange({ notes: e.target.value })}
        className="text-xs"
        placeholder="Notes for this activity (shows in popup as Pollen Dot of Care)"
        data-testid="day-activity-notes"
      />
      {inits.length > 0 && (
        <div className="flex items-center gap-1">
          <Sparkles className="w-3 h-3 text-amber-600" />
          <span className="text-[10px] text-amber-700">Pollen Dot of Care</span>
          {inits.slice(0, 3).map(k => (
            <span key={k} title={k} className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: COL[k] || '#94a3b8' }} />
          ))}
        </div>
      )}
    </div>
  );
};

const ActivityAdder = ({ vendors, onAdd, dayDate }) => {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const activityVendors = useMemo(() => (vendors || []).filter(v => v.type === 'Activity'), [vendors]);
  const matches = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return activityVendors.slice(0, 8);
    return activityVendors.filter(v => (v.name || '').toLowerCase().includes(s)).slice(0, 8);
  }, [q, activityVendors]);

  const exactExists = activityVendors.some(v => (v.name || '').toLowerCase() === q.trim().toLowerCase());

  const pickVendor = (v) => {
    onAdd(v);
    setQ('');
    setOpen(false);
  };

  const createNew = () => {
    if (!q.trim()) return;
    onAdd({ name: q.trim(), id: '', initiatives: [], phone: '' });
    setQ('');
    setOpen(false);
  };

  return (
    <div ref={wrapRef} className="relative" data-testid={`day-activity-adder-${dayDate}`}>
      <div className="flex items-center gap-1.5">
        <Input
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Search or type a new activity..."
          className="h-7 text-xs flex-1"
          data-testid="day-activity-search"
        />
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-7 text-[10px] px-2"
          onClick={createNew}
          disabled={!q.trim()}
          data-testid="day-activity-add-btn"
        >
          <Plus className="w-3 h-3 mr-1" /> Add
        </Button>
      </div>
      {open && (matches.length > 0 || (q.trim() && !exactExists)) && (
        <div className="absolute z-30 left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg max-h-56 overflow-y-auto">
          {matches.map(v => (
            <button
              key={v.id}
              type="button"
              className="w-full text-left px-3 py-1.5 hover:bg-muted text-xs flex items-center gap-2"
              onMouseDown={(e) => { e.preventDefault(); pickVendor(v); }}
              data-testid={`day-activity-suggest-${v.id}`}
            >
              <Compass className="w-3 h-3 text-blue-600 shrink-0" />
              <span className="font-medium truncate flex-1">{v.name}</span>
              {(v.initiatives || []).slice(0, 3).map(k => (
                <span key={k} title={k} className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: COL[k] || '#94a3b8' }} />
              ))}
              {v.location && <span className="text-[10px] text-muted-foreground truncate">{v.location}</span>}
            </button>
          ))}
          {q.trim() && !exactExists && (
            <button
              type="button"
              className="w-full text-left px-3 py-1.5 hover:bg-emerald-50 text-xs flex items-center gap-2 border-t"
              onMouseDown={(e) => { e.preventDefault(); createNew(); }}
              data-testid="day-activity-create-new"
            >
              <Plus className="w-3 h-3 text-emerald-600 shrink-0" />
              Create new activity: <span className="font-medium">"{q.trim()}"</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default DayActivitiesEditor;
